import * as React from 'react';
import { FORM_ERROR } from 'final-form';
import arrayMutators from 'final-form-arrays';
import isEqual from 'lodash/isEqual';
import { useDispatch } from 'react-redux';
import { Form } from '@gandi/form/FinalForm';
import { useResourcePatch } from '@gandi/resource/react';
import { useTranslator } from '@gandi/translate';
import { Alert, Base, Spinner } from '@gandi/ui';
import { DisabledContextProvider, DisabledContext } from '../formUtils/DisabledContext';
import { clearSubmitError } from '../formUtils/mutators';
import { SectionReadOnlyProvider } from '../formUtils/readOnly';
import { useValidate } from '../formUtils/useValidate';
import { mapFormDataToTemplate, mapTemplateToFormData } from '../models/mapData';
import { mapFetchErrorToFormError } from '../models/mapError';
import { prefetchOne, templateResource, useTemplate } from '../modules/templateResource';
import LoadPermission from './LoadPermission';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const noop = initialData => initialData;
const EditForm = ({
  uuid,
  transformInitialValues = noop,
  doSubmit,
  onSubmitSuccess,
  onSubmitFail,
  children,
  ...rest
}) => {
  const dispatch = useDispatch();
  const {
    detail: template,
    isLoaded,
    isFailure
  } = useTemplate(uuid);
  const {
    handlePatch
  } = useResourcePatch(templateResource, {
    id: uuid
  });
  const {
    __
  } = useTranslator();
  const validate = useValidate(template);
  const [initialValues, setInitialValues] = React.useState();
  React.useEffect(() => {
    // reset the form when uuid change
    setInitialValues();
  }, [uuid]);
  React.useEffect(() => {
    // XXX: May be this part can be improved to prevent spinner on first render,
    // since the form has been migrated to finalform
    if (isLoaded && template) {
      // $FlowFixMe[unused-promise]
      Promise.resolve(transformInitialValues(mapTemplateToFormData(template), template)).then(values => setInitialValues(values));
    }
  }, [isLoaded, transformInitialValues, template]);
  const onSubmit = React.useCallback(async formValues => {
    if (!template) {
      // This case should never happen since we can only submit once template is set
      return {
        [FORM_ERROR]: __('Template not found')
      };
    }
    // XXX: pristine (and some other props) doesn't work correctly when fields
    // are unregistered like we do with sections (or in a wizzard).
    // Compute pristine based on deep-equal
    const pristine = isEqual(initialValues, formValues);

    // A template may not be editable (e.g. ”Gandi”'s factory templates)
    // or in case of pristine we bypass the patch process but keep applying userland actions
    if (!template.editable || pristine) {
      if (doSubmit) {
        await doSubmit({
          template,
          formValues
        });
      }
      if (onSubmitSuccess) {
        onSubmitSuccess({
          formValues,
          template
        });
      }
      return undefined;
    }
    try {
      const nextTemplate = mapFormDataToTemplate(formValues,
      // $FlowFixMe[incompatible-call] cf. mapFormDataToTemplate
      template);
      await handlePatch(nextTemplate);
      dispatch(templateResource.invalidate());
      if (doSubmit) {
        // $FlowFixMe[incompatible-call]
        await doSubmit({
          formValues,
          template: nextTemplate
        });
      }
      if (onSubmitSuccess) {
        // $FlowFixMe[incompatible-call]
        onSubmitSuccess({
          formValues,
          template: nextTemplate
        });
      }
      return undefined;
    } catch (err) {
      const errors = mapFetchErrorToFormError(err);
      if (onSubmitFail) {
        onSubmitFail(errors);
      }
      return errors;
    }
  }, [__, dispatch, doSubmit, handlePatch, initialValues, onSubmitFail, onSubmitSuccess, template]);
  if (isFailure) {
    return /*#__PURE__*/_jsx(Base, {
      marginBottom: "base",
      children: /*#__PURE__*/_jsx(Alert, {
        intent: "danger",
        message: __('An error occured when retrieving the template')
      })
    });
  }
  if (!isLoaded || !initialValues) {
    return /*#__PURE__*/_jsx(Spinner, {
      centered: true,
      children: __('Template editor is loading')
    });
  }
  return /*#__PURE__*/_jsxs(DisabledContextProvider
  // $FlowFixMe[incompatible-type] null | undefined
  , {
    template: template,
    children: [template?.sharing_space && /*#__PURE__*/_jsx(LoadPermission, {
      sharingId: template.sharing_space.id
    }), /*#__PURE__*/_jsx(DisabledContext.Consumer, {
      children: ({
        disabled
      }) => disabled && /*#__PURE__*/_jsx(Alert, {
        intent: "warning",
        marginBottom: "small",
        message: __('You do not have the permissions necessary to edit this configuration template but you can choose to apply this template for future registrations or transfers.'),
        title: "Warning"
      })
    }), /*#__PURE__*/_jsx(Base, {
      tagName: "p",
      className: "form-notice form-notice-required",
      marginBottom: "base",
      children: __('* Required fields')
    }), /*#__PURE__*/_jsx(SectionReadOnlyProvider, {
      children: /*#__PURE__*/_jsx(Form
      // this className is required in order have correct rendering of
      // gandi.react-form  «control» components (input or label …)
      , {
        className: "form-stacked",
        initialValues: initialValues,
        onSubmit: onSubmit,
        validate: validate,
        mutators: {
          ...arrayMutators,
          clearSubmitError
        },
        ...rest,
        children: children
      })
    })]
  });
};
EditForm.preFetch = prefetchOne;
export default EditForm;